import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { queryKeys } from './queryKeys';
import type { Tables } from '@/integrations/supabase/types';

export type Account = Tables<'accounts'>;
export type Wallet = Tables<'wallets'>;
export type AccountFundingSource = Tables<'account_funding_sources'>;

/**
 * Fetch all accounts
 * Used by: Accounts page, DashboardStats, transaction forms
 */
export function useAccounts() {
  return useQuery({
    queryKey: queryKeys.accounts.list(),
    queryFn: async () => {
      const { data, error } = await supabase
        .from('accounts')
        .select('*')
        .order('created_at', { ascending: true });

      if (error) throw error;
      return (data || []) as Account[];
    },
    staleTime: 1000 * 60 * 10, // 10 minutes - accounts change less frequently
  });
}

/**
 * Fetch all wallets
 * Used by: Accounts page, DashboardStats
 */
export function useWallets() {
  return useQuery({
    queryKey: queryKeys.wallets.list(),
    queryFn: async () => {
      const { data, error } = await supabase
        .from('wallets')
        .select('*')
        .order('created_at', { ascending: true });

      if (error) throw error;
      return (data || []) as Wallet[];
    },
    staleTime: 1000 * 60 * 10,
  });
}

/**
 * Fetch all account funding sources (ranked list per account)
 */
export function useAccountFundingSources() {
  return useQuery({
    queryKey: queryKeys.accounts.fundingSources(),
    queryFn: async () => {
      const { data, error } = await supabase
        .from('account_funding_sources')
        .select('*')
        .order('account_id')
        .order('priority');

      if (error) throw error;
      return (data || []) as AccountFundingSource[];
    },
    staleTime: 1000 * 60 * 10,
  });
}

/**
 * Combined hook for components that need both accounts and wallets
 * Provides convenient combined loading state
 */
export function useAccountsWithWallets() {
  const accountsQuery = useAccounts();
  const walletsQuery = useWallets();

  return {
    accounts: accountsQuery.data ?? [],
    wallets: walletsQuery.data ?? [],
    isLoading: accountsQuery.isLoading || walletsQuery.isLoading,
    isError: accountsQuery.isError || walletsQuery.isError,
    error: accountsQuery.error || walletsQuery.error,
    refetch: () => {
      accountsQuery.refetch();
      walletsQuery.refetch();
    },
  };
}
